import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { MonthsEditor, MoonsEditor, LinkEditor } from "./StructureEditors";
import { ordToEarth, earthToOrd, describeRecurrence } from "./calUtils";

const Field = ({ label, hint, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}{hint && <p className="text-[11px] text-[#A8A090]">{hint}</p>}</div>;

function initForm(c = {}) {
  return {
    name: c.name || "", description: c.description || "", color: c.color || "#8B5CF6", era: c.era || "",
    day_hours: c.day_hours ?? 24, rate: c.rate ?? 1, weekdays: (c.weekdays || []).join(", "), weekday_offset: c.weekday_offset ?? 0,
    months: c.months?.length ? c.months : Array.from({ length: 12 }, (_, i) => ({ name: `Month ${i + 1}`, days: 30 })),
    leap: c.leap || { every: 0, month: 0, days: 0 }, start_year: c.start_year ?? 1, epoch: ordToEarth(c.epoch_abs ?? 730120),
    moons: c.moons || [], link: c.link || null,
  };
}

function DraftEvents({ events, setEvents, calendar }) {
  if (!events.length) return null;
  return (
    <section className="space-y-2" data-testid="draft-events">
      <Label>Events the AI found ({events.length})</Label>
      {events.map((e, i) => (
        <div key={i} className="flex items-center gap-2 rounded-md border border-[#2D3442] px-3 py-1.5 text-sm" data-testid={`draft-event-${i}`}>
          <span className="text-[#F3EBDD]">{e.title}</span>
          <span className="text-xs text-[#A8A090]">{describeRecurrence(e.recurrence, calendar)}</span>
          <Button type="button" size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={() => setEvents(events.filter((_, j) => j !== i))} data-testid={`draft-event-remove-${i}`}><X className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
    </section>
  );
}

export const CalendarDialog = ({ open, onOpenChange, uid, initial, timelines, draft, onSaved }) => {
  const editing = !!initial?.id;
  const greg = initial?.kind === "gregorian";
  const [f, setF] = useState(() => initForm(draft?.calendar || initial));
  const [events, setEvents] = useState(draft?.events || []);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const names = { month_names: f.months.map((m) => m.name), weekday_names: f.weekdays.split(",").map((s) => s.trim()).filter(Boolean), moons: f.moons };

  const save = async (e) => {
    e.preventDefault();
    const epoch = earthToOrd(f.epoch);
    const structure = greg ? {} : {
      day_hours: Number(f.day_hours) || 24, rate: Number(f.rate) || 1, weekdays: names.weekday_names, weekday_offset: parseInt(f.weekday_offset, 10) || 0,
      months: f.months.map((m, i) => ({ name: m.name || `Month ${i + 1}`, days: parseInt(m.days, 10) || 1 })),
      leap: { every: parseInt(f.leap.every, 10) || 0, month: parseInt(f.leap.month, 10) || 0, days: parseInt(f.leap.days, 10) || 0 },
      start_year: parseInt(f.start_year, 10) || 0, ...(epoch != null ? { epoch_abs: epoch } : {}),
    };
    const body = {
      name: f.name, description: f.description, color: f.color, era: f.era, ...structure,
      moons: f.moons.filter((m) => m.name && Number(m.cycle) > 0).map((m) => ({ name: m.name, cycle: Number(m.cycle), offset: Number(m.offset) || 0 })),
      ...(f.link ? { link: f.link } : editing ? { unlink: true } : {}),
    };
    setBusy(true);
    try {
      const { data } = editing ? await api.patch(`/calendars/${initial.id}`, body)
        : await api.post(`/universes/${uid}/calendars`, { ...body, events, source_entity_id: draft?.calendar?.source_entity_id || null });
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto lf-scroll" data-testid="calendar-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing ? `Edit ${initial.name}` : draft ? "Review drafted calendar" : "New calendar"}</DialogTitle></DialogHeader>
          {draft?.notes && <p className="mt-2 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3 text-sm text-[#F3EBDD]/90" data-testid="draft-notes">{draft.notes}</p>}
          <div className="space-y-5 py-5">
            <div className="grid grid-cols-[1fr_96px_56px] gap-3">
              <Field label="Name"><Input value={f.name} onChange={set("name")} required data-testid="calendar-name-input" /></Field>
              <Field label="Era suffix"><Input value={f.era} onChange={set("era")} placeholder="AR" data-testid="calendar-era-input" /></Field>
              <Field label="Color"><input type="color" value={f.color} onChange={set("color")} className="h-10 w-full cursor-pointer rounded-md border border-[#2D3442] bg-transparent" data-testid="calendar-color-input" /></Field>
            </div>
            <Field label="Description"><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="calendar-description-input" /></Field>
            {greg ? <p className="text-sm text-[#A8A090]" data-testid="calendar-gregorian-note">The Gregorian structure is fixed — it is the reference clock every other calendar converts through.</p> : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Field label="Day length (Earth h)"><Input type="number" step="any" className="font-mono-lf" value={f.day_hours} onChange={set("day_hours")} data-testid="calendar-day-hours-input" /></Field>
                  <Field label="Time rate" hint="1 = Earth pace; 0.5 = half as fast"><Input type="number" step="any" className="font-mono-lf" value={f.rate} onChange={set("rate")} data-testid="calendar-rate-input" /></Field>
                  <Field label="First year"><Input type="number" className="font-mono-lf" value={f.start_year} onChange={set("start_year")} data-testid="calendar-start-year-input" /></Field>
                  <Field label="…began on Earth"><Input type="date" value={f.epoch} onChange={set("epoch")} data-testid="calendar-epoch-input" /></Field>
                </div>
                <div className="grid grid-cols-[1fr_100px] gap-3">
                  <Field label="Weekdays (comma separated, blank = none)"><Input value={f.weekdays} onChange={set("weekdays")} placeholder="Sunday, Moonday, …" data-testid="calendar-weekdays-input" /></Field>
                  <Field label="First weekday #"><Input type="number" className="font-mono-lf" value={f.weekday_offset} onChange={set("weekday_offset")} data-testid="calendar-weekday-offset-input" /></Field>
                </div>
                <MonthsEditor months={f.months} onChange={set("months")} />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Leap year every (yrs)"><Input type="number" min={0} className="font-mono-lf" value={f.leap.every} onChange={(e) => setF({ ...f, leap: { ...f.leap, every: e.target.value } })} data-testid="calendar-leap-every-input" /></Field>
                  <Field label="Leap month #"><Input type="number" min={1} className="font-mono-lf" value={Number(f.leap.month) + 1} onChange={(e) => setF({ ...f, leap: { ...f.leap, month: Math.max((parseInt(e.target.value, 10) || 1) - 1, 0) } })} data-testid="calendar-leap-month-input" /></Field>
                  <Field label="Extra days"><Input type="number" min={0} className="font-mono-lf" value={f.leap.days} onChange={(e) => setF({ ...f, leap: { ...f.leap, days: e.target.value } })} data-testid="calendar-leap-days-input" /></Field>
                </div>
              </>
            )}
            <MoonsEditor moons={f.moons} onChange={set("moons")} />
            <LinkEditor link={f.link} timelines={timelines} onChange={set("link")} />
            {!editing && <DraftEvents events={events} setEvents={setEvents} calendar={names} />}
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="calendar-save-button">{busy ? "Saving…" : "Save calendar"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
